//pages/RiskDisclosure.tsx
import Layout from "@/components/Layout";
// SEO: Import SEO component for risk disclosure structured data.
import SEO from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Mail } from "lucide-react";

export default function RiskDisclosure() {
  const riskSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Risk Disclosure',
    'url': 'https://tradesagepro.com/risk',
    'description': 'Risk disclosure outlining volatility, capital loss, and limitations for AI-powered market research and data analysis outputs on TradeSagePro.',
    'about': { '@type': 'Organization', 'name': 'TradeSagePro' }
  };
  return (
    <Layout>
      {/* SEO TAGS: Risk disclosure page indexed for compliance queries; WebPage schema aids clarity. */}
      <SEO
        title="Risk Disclosure | AI Market Research Transparency"
        description="Review TradeSagePro risk disclosure covering market volatility, no performance guarantees, operational risks, and user responsibility when using AI market research and analysis outputs."
        canonical="https://tradesagepro.com/risk"
        type="article"
        jsonLd={riskSchema}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-[hsl(0,72%,51%)] mr-3" />
              <h1 className="text-4xl font-bold text-white">Risk Disclosure</h1>
            </div>
            <p className="text-sm text-[hsl(210,9%,80%)]">Last Updated: October 12, 2025</p>
          </div>

          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
            <CardContent className="p-8 max-w-none">
              <div className="space-y-10 text-[hsl(210,9%,91%)] leading-relaxed">

                <section id="intro">
                  <p>
                    The AI-powered market research analysis outputs and analytical reasoning provided by JINOVEX LLC ("Company", "we", "our", or "us")
                    are for informational purposes only. They do not constitute financial, investment, legal, or tax advice.
                  </p>
                  <p className="mt-3">By accessing or using the Service, you acknowledge and accept the risks outlined below:</p>
                </section>

                <section id="market-volatility">
                  <h2 className="text-2xl font-bold text-white mb-3">1. Market Volatility and Capital Loss</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Cryptocurrency markets are highly volatile. Prices can fluctuate rapidly and unpredictably.</li>
                    <li>There is a substantial risk of partial or total loss of your invested capital.</li>
                    <li>You must assess your own risk tolerance and financial situation before using the Service.</li>
                  </ul>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="font-semibold text-white mb-1">Applicable Regulations</div>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>GDPR / LGPD: Users must be informed of the risks associated with data-driven financial analysis.</li>
                      <li>CCPA / PIPEDA: Transparency regarding financial risks is required to prevent misleading consumers.</li>
                    </ul>
                  </div>
                </section>

                <section id="no-guarantee">
                  <h2 className="text-2xl font-bold text-white mb-3">2. No Guarantee of Performance</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>AI-generated analysis outputs are based on proprietary algorithms and historical market data.</li>
                    <li>Past performance does not guarantee future results.</li>
                    <li>The Service does not provide assurance of profitability, accuracy, or timeliness of analysis outputs.</li>
                  </ul>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="font-semibold text-white mb-1">Applicable Regulations</div>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>GDPR / LGPD: Users must be clearly informed about the limitations of AI outputs.</li>
                      <li>CCPA / PIPEDA: Disclosure prevents users from assuming guaranteed financial outcomes.</li>
                    </ul>
                  </div>
                </section>

                <section id="limitation-liability">
                  <h2 className="text-2xl font-bold text-white mb-3">3. Limitation of Liability</h2>
                  <p>JINOVEX LLC shall not be liable for any losses or damages resulting from:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-1">
                    <li>Decisions made based on AI analysis outputs or reasoning,</li>
                    <li>Service downtime or technical failures,</li>
                    <li>Errors in data analysis or market predictions.</li>
                  </ul>
                  <p className="mt-2">You assume full responsibility for any financial decisions and acknowledge that all financial risk is borne by you.</p>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="font-semibold text-white mb-1">Applicable Regulations</div>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>GDPR / LGPD: Users must be aware that data-driven outputs do not create liability for the provider.</li>
                      <li>CCPA / PIPEDA: Limitation of liability aligns with consumer protection disclosure standards.</li>
                    </ul>
                  </div>
                </section>

                <section id="regulatory-compliance">
                  <h2 className="text-2xl font-bold text-white mb-3">4. Regulatory Compliance</h2>
                  <p>It is your responsibility to:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-1">
                    <li>Understand local cryptocurrency regulations, taxation, and compliance requirements.</li>
                    <li>Ensure that your trading activities conform to the law in your jurisdiction.</li>
                  </ul>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="font-semibold text-white mb-1">Applicable Regulations</div>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>GDPR / LGPD / CCPA / PIPEDA: Ensures users acknowledge responsibility for legal compliance when using AI-driven financial tools.</li>
                    </ul>
                  </div>
                </section>

                <section id="technical-operational">
                  <h2 className="text-2xl font-bold text-white mb-3">5. Technical and Operational Risks</h2>
                  <p>Service functionality depends on:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-1">
                    <li>Platform uptime, AI algorithm performance, and system integrity.</li>
                    <li>Internet connectivity, device compatibility, and payment processor reliability (Paddle.com).</li>
                    <li>Failures or disruptions may delay or compromise the quality of analysis outputs.</li>
                  </ul>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="font-semibold text-white mb-1">Applicable Regulations</div>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>GDPR / LGPD: Users must be informed about processing risks and system reliability.</li>
                      <li>CCPA / PIPEDA: Users must be aware of potential operational interruptions that could affect service delivery.</li>
                    </ul>
                  </div>
                </section>

                <section id="acknowledgment">
                  <h2 className="text-2xl font-bold text-white mb-3">6. Risk Acknowledgment</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>You explicitly acknowledge and accept all risks associated with engaging with digital asset markets.</li>
                    <li>You confirm that you will not risk funds that you cannot afford to lose.</li>
                    <li>You agree to seek independent professional advice if needed.</li>
                  </ul>
                </section>

                <section id="contact">
                  <h2 className="text-2xl font-bold text-white mb-3">Contact Us</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="font-semibold text-white mb-1 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</div>
                      <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:info@tradesagepro.com">info@tradesagepro.com</a>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="font-semibold text-white mb-1">Registered Address</div>
                      <div className="text-[hsl(210,9%,85%)]">JINOVEX LLC – Online Service Operations, Tbilisi, Georgia (operating globally)</div>
                    </div>
                  </div>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
